import React from 'react';
import { motion } from 'framer-motion';

const companyHighlights = [
  {
    icon: '🏢',
    title: 'ISO & PSARA Certified',
    subtitle: 'Government approved security standards',
    color: '#1976D2',
    bg: '#EAF7FB',
  },
  {
    icon: '⚡',
    title: '24/7 Rapid Deployment',
    subtitle: 'Pan-Bihar & regional operational network',
    color: '#00B8D9',
    bg: '#E0F7FA',
  },
  {
    icon: '🛡️',
    title: '100% Verified Manpower',
    subtitle: 'Strict background & police verified staff',
    color: '#059669',
    bg: '#ECFDF5',
  },
  {
    icon: '📈',
    title: 'Statutory Compliance',
    subtitle: 'PF, ESIC, GST & labor law compliant',
    color: '#8B5CF6',
    bg: '#F5F3FF',
  },
];

const leadershipMembers = [
  {
    name: 'Rakesh Bharti',
    designation: 'Director & Founder',
    image: `${process.env.PUBLIC_URL}/images/leadership/rakesh-bharti.jpg`,
    alt: 'Director Rakesh Bharti',
    roleTag: 'Executive Leadership',
    description: 'A seasoned security and operations strategist driving enterprise standards, disciplined workforce deployment, client satisfaction, and corporate transparency across North India.',
    highlights: ['8+ Years Industry Experience', 'Specialized in Govt & Corporate Security', 'Guaranteed Quality Audits'],
  }
];

const DirectorDetails = () => {
  return (
    <section
      id="director-details"
      className="relative pt-16 pb-12 md:pt-20 md:pb-14 px-4 sm:px-6 overflow-hidden w-full"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F4FBFE 50%, #FFFFFF 100%)',
      }}
    >
      {/* Subtle crisp geometric pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: 'radial-gradient(#1976D2 0.75px, transparent 0.75px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto w-full">

        {/* ===== COMPANY PROFILE 3D SHOWCASE ===== */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          {/* Top Pill Tag */}
          <div className="inline-flex items-center gap-2 bg-[#EAF7FB] border border-[#2C61B924] text-[#1976D2] text-xs font-bold tracking-[0.14em] uppercase mb-4 px-5 py-2 rounded-full shadow-sm">
            <span className="w-2 h-2 rounded-full bg-[#1976D2]" />
            About AVISS India
          </div>

          {/* Premium Italic Styled Display Heading */}
          <h2 className="text-[#001726] font-extrabold tracking-tight leading-[1.15] mb-4 text-3xl sm:text-4xl md:text-5xl">
            Company <span className="text-[#1976D2] italic font-serif font-semibold tracking-normal">Profile</span>
          </h2>
          <div className="mx-auto mb-4 h-1 w-20 rounded-full bg-gradient-to-r from-[#6FCEEC] to-[#1976D2]" />
          <p className="text-[#64748B] text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed font-normal">
            Trusted security, facility management, and skilled manpower partner for government institutions and corporate enterprises.
          </p>

          {/* 3D Elevated Profile Hero Card */}
          <div className="relative bg-white rounded-[28px] border border-[#2C61B924] p-7 sm:p-10 md:p-12 shadow-[0_20px_50px_rgba(25,118,210,0.08)] hover:shadow-[0_25px_60px_rgba(25,118,210,0.14)] transition-all duration-300 max-w-5xl mx-auto text-left overflow-hidden">
            {/* Top gradient highlight strip */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#6FCEEC] via-[#1976D2] to-[#00B8D9]" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

              {/* Left Editorial Copy */}
              <div className="lg:col-span-8">
                <span className="text-xs font-bold tracking-[0.1em] text-[#1976D2] uppercase block mb-2">
                  Official Entity
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-[#001726] leading-snug mb-4">
                  Aryavihhar Intelligence and Security Services Private Limited <span className="text-[#1976D2]">(AVISS)</span>
                </h3>

                <p className="text-[#475569] text-sm sm:text-base md:text-[1.05rem] leading-[1.8] m-0 mb-6">
                  AVISS is a government-licensed and ISO-certified management enterprise delivering high-vigilance physical security, armed guarding, mechanized housekeeping, ICT laboratory operations, and verified manpower outsourcing. We power secure, reliable operations across government secretariats, educational institutions, hospitals, and Fortune-affiliated corporate sites throughout Bihar and beyond.
                </p>

                {/* Quick Trust Badges */}
                <div className="flex flex-wrap gap-2.5">
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#F8FAFC] border border-[#2C61B924] text-xs font-semibold text-[#001726]">
                    ✓ PSARA Licensed
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#F8FAFC] border border-[#2C61B924] text-xs font-semibold text-[#001726]">
                    ✓ ISO 9001:2015
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#F8FAFC] border border-[#2C61B924] text-xs font-semibold text-[#001726]">
                    ✓ MSME & Startup India
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#F8FAFC] border border-[#2C61B924] text-xs font-semibold text-[#001726]">
                    ✓ 100% PF & ESIC Verified
                  </span>
                </div>
              </div>

              {/* Right 3D Visual Pill Grid */}
              <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3.5">
                {companyHighlights.map((hl, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-2xl bg-[#F8FAFC] border border-[#2C61B918] hover:border-[#1976D2] hover:bg-white hover:shadow-md transition-all duration-200 flex items-start gap-3.5"
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xl shadow-sm"
                      style={{ background: hl.bg }}
                    >
                      {hl.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#001726] m-0 mb-0.5 leading-tight">
                        {hl.title}
                      </h4>
                      <p className="text-xs text-[#64748B] m-0 leading-normal">
                        {hl.subtitle}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </motion.div>

        {/* ===== LEADERSHIP SECTION ===== */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-flex items-center justify-center mx-auto rounded-full border border-[#2C61B924] bg-[#EAF7FB] text-[#1976D2] text-xs font-bold tracking-[0.14em] uppercase mb-3 px-5 py-2 shadow-sm">
            Executive Leadership
          </span>
          <h2 className="text-[#001726] font-extrabold leading-tight mb-3 text-3xl sm:text-4xl md:text-5xl">
            Our <span className="text-[#1976D2] italic font-serif font-semibold tracking-normal">Leadership</span>
          </h2>
          <div className="mx-auto mb-4 h-1 w-20 rounded-full bg-gradient-to-r from-[#6FCEEC] to-[#1976D2]" />
          <p className="text-[#64748B] text-base md:text-lg max-w-xl mx-auto font-normal">
            Seasoned industry pioneers ensuring strict discipline, regulatory compliance, and unwavering client trust.
          </p>
        </motion.div>

        {/* ===== 3D LEADERSHIP CARD ===== */}
        <div className="flex justify-center max-w-3xl mx-auto">
          {leadershipMembers.map((member) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6, transition: { duration: 0.22 } }}
              className="w-full bg-white rounded-[28px] overflow-hidden border border-[#2C61B924] hover:border-[#1976D2] shadow-[0_15px_40px_rgba(0,23,38,0.06)] hover:shadow-[0_20px_50px_rgba(25,118,210,0.14)] transition-all duration-300"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 items-center">

                {/* Photo / Emblem Col */}
                <div className="md:col-span-5 p-7 sm:p-8 flex flex-col items-center justify-center bg-gradient-to-b from-[#F4FBFE] to-white border-b md:border-b-0 md:border-r border-[#2C61B918]">
                  <div className="relative w-40 h-40 sm:w-44 sm:h-44 rounded-2xl overflow-hidden p-4 bg-white border border-[#2C61B924] shadow-md flex items-center justify-center group">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/logo.png`}
                      alt="AVISS New Official Logo"
                      className="w-full h-full object-contain transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <span className="mt-4 px-3.5 py-1 rounded-full bg-[#EAF7FB] text-[#1976D2] text-[11px] font-bold tracking-wider uppercase border border-[#2C61B924]">
                    {member.roleTag}
                  </span>
                </div>

                {/* Details Col */}
                <div className="md:col-span-7 p-7 sm:p-8 text-left">
                  <h3 className="text-2xl sm:text-[1.7rem] font-bold text-[#001726] mb-1 tracking-tight">
                    {member.name}
                  </h3>
                  <p className="text-[#1976D2] text-sm font-bold tracking-wide uppercase mb-4">
                    {member.designation}
                  </p>

                  <p className="text-[#475569] text-sm sm:text-base leading-relaxed mb-5">
                    {member.description}
                  </p>

                  <div className="flex flex-col gap-2 pt-4 border-t border-[#2C61B918]">
                    {member.highlights.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs font-semibold text-[#001726]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1976D2]" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default DirectorDetails;